import { useEffect, useRef, useState } from "react";
import { Skeleton } from "@libertify/ui";
import { useGo, useLogout, useTranslate } from "@refinedev/core";
import { Button, Dropdown } from "antd";
import {
  SignOutIcon,
  DotsThreeVerticalIcon,
  TrashIcon,
  CheckCircleIcon,
} from "@phosphor-icons/react";
import { LibertifyLogo } from "pages/auth/components/LibertifyLogo";
import useInitSubscription from "pages/pricing/hooks/useInitSubscription";
import { useOrganization } from "hooks/useOrganization";
import { useTrackOnce } from "hooks/useAnalytics";
import { AnalyticsEvents } from "types/analytics";
import { useStripePricingScript } from "@/hooks/useStripePricingScript";
import { useSubscriptionPolling } from "@/hooks/useSubscriptionPolling";
import { useSoftPaywall } from "@/providers/SoftPaywallProvider";
import {
  PagePW,
  LeftColPW,
  RightColPW,
  LogoBarPW,
  EyebrowPW,
  HeroPW,
  HeroSubPW,
  TimelineGridPW,
  TimelineCardPW,
  DayBadgePW,
  TimelineTitlePW,
  TimelineDescPW,
  StickyWrapPW,
  PricingCardPW,
  TopBarPW,
  FeaturesSectionTitlePW,
  FeaturesGridPW,
  FeaturesColPW,
  FeatureItemPW,
  CtaDescriptionPW,
  CtaButtonPW,
  CtaSubtextPW,
  HeroImagePW,
  FeaturesCardPW,
} from "./PaywallPage.styles";

const TIMELINE_ITEMS = [
  { key: "creation", accent: "#14b8a6" },
  { key: "improvement", accent: "#6d28d9" },
  { key: "analytics", accent: "#b91bbf" },
] as const;

const FEATURE_KEYS_LEFT = [
  "documentToVideo",
  "aiChatbot",
  "journeyEngine",
  "brandedExperiences",
] as const;

const FEATURE_KEYS_RIGHT = [
  "aiAvatar",
  "fullAnalytics",
  "customVoice",
  "shareableLink",
  "keepAccess",
] as const;

const PaywallPage = () => {
  const t = useTranslate();
  const { organization } = useOrganization({});
  const { params, isLoading, init } = useInitSubscription({
    organizationId: organization?.id ?? "",
  });

  useStripePricingScript();

  const hasInitialized = useRef(false);
  useEffect(() => {
    if (organization?.id && !hasInitialized.current) {
      hasInitialized.current = true;
      init();
    }
  }, [organization?.id, init]);

  const go = useGo();
  const { subscriptions } = useSubscriptionPolling();

  useEffect(() => {
    if (subscriptions?.license_type && subscriptions.license_type !== "Free") {
      go({ to: "/" });
    }
  }, [subscriptions?.license_type, go]);

  const { mutate: mutateLogout } = useLogout();
  const [menuOpen, setMenuOpen] = useState(false);

  const { paywallFlow: flow } = useSoftPaywall();

  // Users in hard_no_card_3d or soft_3_exp who reach the paywall page
  // have exhausted their trial (3 days or 3 experiences) — show expired layout
  const isExpiredTrial = flow === "hard_no_card_3d" || flow === "soft_3_exp";

  const VARIANT_KEYS = {
    control: {
      heroTitle: "pages.paywall.heroTitle",
      heroSubtitle: "pages.paywall.heroSubtitle",
    },
    hard_no_card_3d: {
      heroTitle: "pages.paywall.expiredTrialPaywall.heroTitle",
      heroSubtitle: "",
    },
    soft_3_exp: {
      heroTitle: "pages.paywall.expiredTrialPaywall.heroTitle",
      heroSubtitle: "",
    },
  } as const;
  const variantKeys =
    VARIANT_KEYS[flow as keyof typeof VARIANT_KEYS] ?? VARIANT_KEYS.control;

  const pricingTableMap: Record<string, string> = {
    control: import.meta.env.VITE_STRIPE_PRICING_TABLE_ID_ONLY_ONE,
    soft_3_exp: import.meta.env.VITE_STRIPE_PRICING_TABLE_ID_SOFT,
    hard_no_card_3d: import.meta.env.VITE_STRIPE_PRICING_TABLE_ID_HARD_NO_CARD,
  };
  const pricingTableId =
    pricingTableMap[flow] ??
    import.meta.env.VITE_STRIPE_PRICING_TABLE_ID_ONLY_ONE;
  const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

  useTrackOnce(AnalyticsEvents.PaywallView, {
    paywall_flow: flow,
    paywall_hard: true,
    expired_trial: isExpiredTrial,
    pricing_table_id: pricingTableId,
  });

  useTrackOnce(AnalyticsEvents.PricingView, {
    container: "paywall",
    pricing_table_id: pricingTableId,
  });

  return (
    <PagePW>
      <TopBarPW>
        <Button
          type="text"
          icon={<SignOutIcon size={20} />}
          onClick={() => mutateLogout()}
        >
          {t("buttons.logout")}
        </Button>
        <Dropdown
          menu={{
            items: [
              {
                key: "delete-account",
                danger: true,
                icon: <TrashIcon size={16} />,
                label: t("pages.user.edit.deleteAccount"),
                onClick: () => {
                  setMenuOpen(false);
                  go({ to: "/settings" });
                },
              },
            ],
          }}
          trigger={["click"]}
          placement="bottomRight"
          open={menuOpen}
          onOpenChange={setMenuOpen}
        >
          <Button
            type="text"
            icon={<DotsThreeVerticalIcon size={20} weight="bold" />}
          />
        </Dropdown>
      </TopBarPW>
      <LeftColPW>
        <LogoBarPW>
          <LibertifyLogo />
        </LogoBarPW>

        <EyebrowPW>
          {isExpiredTrial
            ? t("pages.paywall.expiredTrialPaywall.eyebrow")
            : t("pages.paywall.eyebrow")}
        </EyebrowPW>
        <HeroPW>{t(variantKeys.heroTitle)}</HeroPW>
        {!isExpiredTrial && (
          <HeroSubPW>{t(variantKeys.heroSubtitle)}</HeroSubPW>
        )}

        {isExpiredTrial ? (
          <>
            <HeroImagePW />
            <FeaturesCardPW>
              <FeaturesSectionTitlePW>
                {t("pages.paywall.expiredTrialPaywall.featuresTitle")}
              </FeaturesSectionTitlePW>
              <FeaturesGridPW>
                <FeaturesColPW>
                  {FEATURE_KEYS_LEFT.map((key) => (
                    <FeatureItemPW key={key}>
                      <CheckCircleIcon
                        size={18}
                        weight="fill"
                        color="#14b8a6"
                      />
                      {t(`pages.paywall.expiredTrialPaywall.features.${key}`)}
                    </FeatureItemPW>
                  ))}
                </FeaturesColPW>
                <FeaturesColPW>
                  {FEATURE_KEYS_RIGHT.map((key) => (
                    <FeatureItemPW key={key}>
                      <CheckCircleIcon
                        size={18}
                        weight="fill"
                        color="#14b8a6"
                      />
                      {t(`pages.paywall.expiredTrialPaywall.features.${key}`)}
                    </FeatureItemPW>
                  ))}
                </FeaturesColPW>
              </FeaturesGridPW>
            </FeaturesCardPW>
            <CtaDescriptionPW>
              {t("pages.paywall.expiredTrialPaywall.ctaDescription")}
            </CtaDescriptionPW>
          </>
        ) : (
          <TimelineGridPW>
            {TIMELINE_ITEMS.map((item) => (
              <TimelineCardPW key={item.key} $accent={item.accent}>
                <DayBadgePW $accent={item.accent}>
                  {t(`pages.paywall.timeline.${item.key}.badge`)}
                </DayBadgePW>
                <TimelineTitlePW>
                  {t(`pages.paywall.timeline.${item.key}.title`)}
                </TimelineTitlePW>
                <TimelineDescPW>
                  {t(`pages.paywall.timeline.${item.key}.description`)}
                </TimelineDescPW>
              </TimelineCardPW>
            ))}
          </TimelineGridPW>
        )}
      </LeftColPW>

      <RightColPW>
        <StickyWrapPW>
          <PricingCardPW>
            {isLoading || !params ? (
              <Skeleton variant="rectangular" width="100%" height={546} />
            ) : (
              <stripe-pricing-table
                pricing-table-id={pricingTableId}
                publishable-key={publishableKey}
                customer-session-client-secret={
                  params.customer_session_client_secret ?? undefined
                }
                client-reference-id={params.client_reference_id}
                customer-email={params.customer_email}
              />
            )}
          </PricingCardPW>
          {isExpiredTrial && (
            <>
              <CtaButtonPW>
                {t("pages.paywall.expiredTrialPaywall.ctaButton")}
              </CtaButtonPW>
              <CtaSubtextPW>
                {t("pages.paywall.expiredTrialPaywall.ctaSubtext")}
              </CtaSubtextPW>
            </>
          )}
        </StickyWrapPW>
      </RightColPW>
    </PagePW>
  );
};

export default PaywallPage;
